import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { gbp, num, gbpParen } from "@/lib/format";
import { Warehouse, Truck, Siren, Receipt, PiggyBank, Printer, Boxes } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
  LineChart, Line,
} from "recharts";

const COMP_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#d97706", "#dc2626", "#059669"];

const WsRow = ({ label, row, bold, indent, highlight, tone, picks, qty, deduction }) => {
  const leftBg = highlight ? "bg-blue-50 dark:bg-blue-950/40" : bold ? "bg-slate-50 dark:bg-slate-800/60" : "bg-white dark:bg-slate-900";
  const fmt = deduction ? gbpParen : gbp;
  return (
    <tr className={`${highlight ? "bg-blue-50 dark:bg-blue-950/40" : bold ? "bg-slate-50 dark:bg-slate-800/60" : ""} ${bold ? "font-semibold" : ""} border-t border-slate-100 dark:border-slate-800`}>
      <td className={`sticky left-0 z-10 px-3 py-2 whitespace-nowrap ${indent ? "pl-6" : ""} ${leftBg} ${tone || ""}`}>{label}</td>
      {row.values.map((v, i) => <td key={i} className={`px-3 py-2 text-right font-mono-num ${tone || ""}`}>{v ? fmt(v) : <span className="text-slate-300 dark:text-slate-600">—</span>}</td>)}
      <td className={`px-3 py-2 text-right font-mono-num font-semibold ${highlight ? "" : "bg-slate-50 dark:bg-slate-800/40"} ${tone || ""}`}>{fmt(row.total)}</td>
      <td className="px-3 py-2 text-right font-mono-num text-slate-500 bg-slate-50/60 dark:bg-slate-800/30">{picks !== undefined && picks !== null ? num(picks) : ""}</td>
      <td className="px-3 py-2 text-right font-mono-num text-slate-500 bg-slate-50/60 dark:bg-slate-800/30">{qty !== undefined && qty !== null ? num(qty) : ""}</td>
    </tr>
  );
};

const SectionRow = ({ label, span }) => (
  <tr><td colSpan={span} className="sticky left-0 bg-slate-100 dark:bg-slate-800 px-3 py-2 font-display font-semibold text-slate-700 dark:text-slate-200 text-[13px] uppercase tracking-wide">{label}</td></tr>
);

export default function SiteSummary() {
  const { data, isLoading } = useQuery({ queryKey: ["site-summary"], queryFn: () => api.get("/site-summary").then((r) => r.data) });
  const [site, setSite] = useState("");
  const [fy, setFy] = useState("FY27");
  const [period, setPeriod] = useState("all");

  const sites = data?.sites || [];

  useEffect(() => {
    if (!site && sites.length) {
      const preferred = sites.find((s) => s.site === "IPL Normanton");
      setSite(preferred ? preferred.site : sites[0].site);
    }
  }, [sites, site]);

  const current = useMemo(() => sites.find((s) => s.site === site), [sites, site]);

  const { data: bd } = useQuery({
    queryKey: ["site-breakdown", site, fy],
    queryFn: () => api.get("/site-breakdown", { params: { site, fy } }).then((r) => r.data),
    enabled: !!site,
  });

  const { data: vc } = useQuery({
    queryKey: ["site-volume-costs", site, fy],
    queryFn: () => api.get("/site-volume-costs", { params: { site, fy, basis: "Case" } }).then((r) => r.data),
    enabled: !!site,
  });

  const { data: ws } = useQuery({
    queryKey: ["site-worksheet", site, fy],
    queryFn: () => api.get("/site-worksheet", { params: { site, fy, basis: "Case" } }).then((r) => r.data),
    enabled: !!site,
  });

  const { data: pf } = useQuery({
    queryKey: ["site-pf-worksheet", site, fy],
    queryFn: () => api.get("/pulled-forward/worksheet", { params: { site, fy } }).then((r) => r.data),
    enabled: !!site,
  });

  const fyOptions = bd?.fy_options || ["FY27"];
  const periodsAll = bd?.periods || [];
  const periodOpts = periodsAll.map((p) => p.period_label);
  const periodRows = period === "all" ? periodsAll : periodsAll.filter((p) => p.period_label === period);

  const compData = bd ? [
    { name: "Picking", value: bd.components.picking },
    { name: "Storage", value: bd.components.storage },
    { name: "Movement", value: bd.components.movement },
    { name: "Delivery", value: bd.components.delivery },
    { name: "Emergency", value: bd.components.emergency },
    { name: "Activity", value: bd.components.activity },
  ] : [];

  const trendData = periodRows.map((p) => ({ name: p.month_label, "Cost of Goods": p.cost_of_goods, Savings: p.savings }));

  // Savings momentum: cumulative savings across ALL periods of the year (unfiltered)
  const savingsTrend = useMemo(() => {
    let cum = 0;
    return periodsAll.map((p) => {
      cum += p.savings;
      return { name: p.month_label, Savings: p.savings, "Cumulative": Math.round(cum * 100) / 100 };
    });
  }, [periodsAll]);

  const volPeriods = vc?.periods || [];
  const volRows = period === "all" ? volPeriods : volPeriods.filter((p) => p.period_label === period);
  const volChart = volRows.map((p) => ({
    name: p.month_label, Picking: p.picking_cost, Storage: p.storage_cost, Movement: p.movement_cost, Delivery: p.delivery_cost,
  }));
  const volTotals = vc?.totals || {};

  if (isLoading) return <Loading />;

  const downloadPack = async () => {
    try {
      toast.loading("Generating PDF…", { id: "pdf" });
      const res = await api.get("/site-pack/pdf", { params: { site, fy, basis: "Case" }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${site.replace(/\s+/g, "-")}-${fy}-site-pack.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
      toast.success("Site pack downloaded", { id: "pdf" });
    } catch (err) {
      toast.error("Could not generate PDF", { id: "pdf" });
    }
  };

  const Metric = ({ icon: Icon, label, value, sub, tint }) => (
    <Card className="p-5 border-slate-200 dark:border-slate-800">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <p className="mt-4 text-2xl font-display font-bold font-mono-num text-slate-900 dark:text-white">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Card>
  );

  return (
    <div>
      <PageHeader title="Site Summary" subtitle="Full per-DC view (e.g. IPL Normanton) — rate components, cost of goods and savings, filterable by year and period.">
        <Select value={site} onValueChange={setSite}>
          <SelectTrigger className="w-full sm:w-48" data-testid="site-select"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent className="max-h-72">{sites.map((s) => <SelectItem key={s.site} value={s.site}>{s.site}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fy} onValueChange={(v) => { setFy(v); setPeriod("all"); }}>
          <SelectTrigger className="w-full sm:w-32" data-testid="site-fy-select"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full sm:w-44" data-testid="site-period-select"><SelectValue placeholder="Period" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All periods</SelectItem>{periodOpts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={downloadPack} data-testid="site-print-button" className="no-print"><Printer className="w-4 h-4 mr-1.5" /> Download PDF pack</Button>
      </PageHeader>

      {!current ? <EmptyState message="Select a site to view its summary." /> : (
        <div id="site-pack">
          <div className="mb-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Warehouse className="w-6 h-6" /></div>
            <div>
              <h2 className="font-display font-bold text-xl text-slate-900 dark:text-white" data-testid="site-name">{current.site}</h2>
              <p className="text-sm text-slate-500">{fy}{period !== "all" ? ` · ${period}` : ""} · {current.rate_count} applicable rate lines</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-8">
            <Metric icon={Truck} label="Delivery Rate (Y1)" value={gbp(current.delivery_y1)} sub={`Emergency ${gbp(current.emergency_y1)}`} tint="bg-blue-50 text-blue-600" />
            <Metric icon={Receipt} label="Cost of Goods" value={gbp(periodRows.reduce((a, b) => a + b.cost_of_goods, 0))} sub={`${period === "all" ? "all periods" : period}`} tint="bg-amber-50 text-amber-600" />
            <Metric icon={PiggyBank} label="Savings" value={gbp(periodRows.reduce((a, b) => a + b.savings, 0))} sub={`Pulled fwd ${gbp(current.pulled_forward_total)}`} tint="bg-emerald-50 text-emerald-600" />
            <Metric icon={Siren} label="Picks" value={num(periodRows.reduce((a, b) => a + b.picks, 0))} sub="in selected range" tint="bg-violet-50 text-violet-600" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card className="p-5 border-slate-200 dark:border-slate-800">
              <h3 className="font-display font-semibold mb-4 text-slate-900 dark:text-white">Rate components (£ per unit)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={compData} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => gbp(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {compData.map((e, i) => <Cell key={i} fill={COMP_COLORS[i % COMP_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200 dark:border-slate-800">
              <h3 className="font-display font-semibold mb-4 text-slate-900 dark:text-white">Cost of Goods vs Savings by period</h3>
              {trendData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={trendData} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => gbp(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Cost of Goods" fill="#d97706" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Savings" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyState message="No cost/savings data for this site in this year." />}
            </Card>
          </div>

          <Card className="p-5 border-slate-200 dark:border-slate-800 mb-8">
            <h3 className="font-display font-semibold mb-1 text-slate-900 dark:text-white">Savings momentum — {current.site}</h3>
            <p className="text-xs text-slate-500 mb-4">Per-period savings vs running cumulative total across {fy}.</p>
            {savingsTrend.some((d) => d.Savings || d.Cumulative) ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={savingsTrend} margin={{ top: 4, right: 12, left: -12, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => gbp(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Savings" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Cumulative" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState message="No savings recorded for this site in this year." />}
          </Card>

          <Card className="p-5 border-slate-200 dark:border-slate-800 mb-8">
            <div className="flex items-center gap-2 mb-1">
              <Boxes className="w-4 h-4 text-blue-600" />
              <h3 className="font-display font-semibold text-slate-900 dark:text-white">Volume-based costs (Case basis)</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">Picking, storage, movement and delivery costs computed from actual volumes × rate card. Upload volumes on the Import &amp; Export page.</p>
            {volTotals.has_volumes ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
                  {[["Picking", volTotals.picking_cost, "text-blue-600"], ["Storage", volTotals.storage_cost, "text-cyan-600"], ["Movement", volTotals.movement_cost, "text-amber-600"], ["Delivery", volTotals.delivery_cost, "text-violet-600"], ["Total", volTotals.total, "text-emerald-600"]].map(([l, v, c]) => (
                    <div key={l} className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
                      <p className="text-xs text-slate-500">{l}</p>
                      <p className={`text-lg font-bold font-mono-num ${c}`}>{gbp(v)}</p>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={volChart} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => gbp(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Picking" stackId="a" fill="#2563eb" />
                    <Bar dataKey="Storage" stackId="a" fill="#0891b2" />
                    <Bar dataKey="Movement" stackId="a" fill="#d97706" />
                    <Bar dataKey="Delivery" stackId="a" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : <EmptyState message="No volumes uploaded for this site yet — add them via Import & Export → Site Volumes." />}
          </Card>

          <h3 className="font-display font-semibold text-lg mb-1 text-slate-900 dark:text-white">Reconciliation worksheet — {current.site}</h3>
          <p className="text-xs text-slate-500 mb-3">Laid out like the source worksheet: months run left to right, with the cost and fee breakdown down the side. {fy} · Case basis.</p>
          {ws && ws.periods?.length ? (
            <TableShell>
              <table className="text-sm border-collapse" data-testid="site-worksheet-table">
                <thead>
                  <tr className="bg-slate-800 text-white text-xs">
                    <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left font-semibold min-w-[190px]">{current.site}</th>
                    {ws.periods.map((p) => (
                      <th key={p.period_label} className="px-3 py-2.5 text-right font-semibold whitespace-nowrap min-w-[92px]" title={`${p.period_label} · ${p.start} → ${p.end}`}>{p.month_label}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right font-semibold min-w-[100px] bg-slate-900">Total</th>
                    <th className="px-3 py-2.5 text-right font-semibold min-w-[80px] bg-slate-900 border-l border-slate-700">Picks</th>
                    <th className="px-3 py-2.5 text-right font-semibold min-w-[90px] bg-slate-900">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  <SectionRow label="Cost of Goods" span={ws.periods.length + 4} />
                  {ws.cogs_rows.map((r) => <WsRow key={r.name} label={r.name} row={r} indent picks={r.picks} qty={r.quantity} />)}
                  <WsRow label={ws.cogs_total.name} row={ws.cogs_total} bold picks={ws.cogs_total.picks} qty={ws.cogs_total.quantity} />

                  <SectionRow label="Service Fees" span={ws.periods.length + 4} />
                  <tr className="text-slate-500">
                    <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 pl-6 whitespace-nowrap">Days in period</td>
                    {ws.periods.map((p) => <td key={p.period_label} className="px-3 py-2 text-right font-mono-num">{num(p.days)}</td>)}
                    <td className="px-3 py-2 text-right font-mono-num bg-slate-50 dark:bg-slate-800/40">{num(ws.periods.reduce((a, b) => a + b.days, 0))}</td>
                    <td className="bg-slate-50/60 dark:bg-slate-800/30" /><td className="bg-slate-50/60 dark:bg-slate-800/30" />
                  </tr>
                  {ws.fee_rows.map((r) => <WsRow key={r.name} label={r.name} row={r} indent />)}
                  <WsRow label={ws.fees_total.name} row={ws.fees_total} bold />

                  <SectionRow label="Adjustments & Net" span={ws.periods.length + 4} />
                  {ws.credits_row && <WsRow label={ws.credits_row.name} row={ws.credits_row} indent tone="text-rose-600" deduction />}
                  <WsRow label={ws.savings_row.name} row={ws.savings_row} indent tone="text-emerald-600" deduction />
                  <WsRow label={ws.net_row.name} row={ws.net_row} bold highlight />
                </tbody>
              </table>
            </TableShell>
          ) : <EmptyState message="No worksheet data for this site in this year." />}
          {ws && !ws.has_volumes && <p className="text-xs text-slate-400 mt-2">Tip: Service fee rows populate once you upload volumes for this site under Import &amp; Export → Site Volumes.</p>}

          {pf && (
            <div className="mt-8">
              <h3 className="font-display font-semibold text-lg mb-1 text-slate-900 dark:text-white">Pulled forward savings — {current.site}</h3>
              <p className="text-xs text-slate-500 mb-3">Running balance carried across {fy}. Edit these on the Savings &amp; Service Credits page.</p>
              <TableShell>
                <table className="text-sm border-collapse" data-testid="site-pf-table">
                  <thead>
                    <tr className="bg-slate-800 text-white text-xs">
                      <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left min-w-[170px]">{current.site}</th>
                      <th className="px-3 py-2.5 text-right min-w-[88px]">O/B</th>
                      {pf.months.map((m) => <th key={m} className="px-3 py-2.5 text-right min-w-[88px] whitespace-nowrap">{m}</th>)}
                      <th className="px-3 py-2.5 text-right bg-slate-900 min-w-[96px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-slate-500 border-t border-slate-100 dark:border-slate-800">
                      <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2">Weeks in period</td>
                      <td />
                      {pf.months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num">{pf.weeks[i]}</td>)}
                      <td className="px-3 py-2 text-right font-mono-num bg-slate-50 dark:bg-slate-800/40">{pf.weeks.reduce((a, b) => a + b, 0)}</td>
                    </tr>
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 font-medium text-blue-700 dark:text-blue-400">Pulled Forward Saving</td>
                      <td className="px-3 py-2 text-right font-mono-num">{gbp(pf.opening_balance)}</td>
                      {pf.months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num text-blue-600">{gbp(pf.pulled_forward[i])}</td>)}
                      <td className="px-3 py-2 text-right font-mono-num font-semibold text-blue-700 bg-slate-50 dark:bg-slate-800/40">{gbp(pf.pulled_forward_total)}</td>
                    </tr>
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 font-medium text-emerald-700 dark:text-emerald-400">Savings in period</td>
                      <td className="px-3 py-2 text-right font-mono-num text-slate-300">{gbp(0)}</td>
                      {pf.months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num text-emerald-600">{gbp(pf.savings_in_period[i])}</td>)}
                      <td className="px-3 py-2 text-right font-mono-num font-semibold text-emerald-700 bg-slate-50 dark:bg-slate-800/40">{gbp(pf.savings_total)}</td>
                    </tr>
                    <tr className="border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-800/60 px-3 py-2">Closing Balance</td>
                      <td className="px-3 py-2 text-right font-mono-num">{gbp(pf.opening_balance)}</td>
                      {pf.months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num">{gbp(pf.closing_balance[i])}</td>)}
                      <td className="px-3 py-2 text-right font-mono-num bg-slate-100 dark:bg-slate-900">{gbp(pf.closing_final)}</td>
                    </tr>
                  </tbody>
                </table>
              </TableShell>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
